import type { ElementType } from "react";
import { PartyPopper } from "lucide-react";
import { motion, useReducedMotion } from "motion/react";
import { Button } from "@/frontend/components/ui/button";

const CONFETTI_COLORS = ["#10b981", "#d7ff64", "#E4405F", "#f59e0b", "#3b82f6", "#173f2d"];
// Fixed spread so the burst looks the same on every render.
const CONFETTI = Array.from({ length: 28 }, (_, index) => {
  const angle = (index / 28) * Math.PI * 2;
  const distance = 90 + ((index * 37) % 70);
  return {
    x: Math.cos(angle) * distance,
    y: Math.sin(angle) * distance * 0.7 - 40,
    rotate: ((index * 53) % 360) - 180,
    color: CONFETTI_COLORS[index % CONFETTI_COLORS.length],
    round: index % 3 === 0,
    delay: (index % 5) * 0.03,
  };
});

interface FirstWinCelebrationProps {
  title: string;
  /** Lets a dialog render the heading as its own title. */
  titleAs?: ElementType;
  body: string;
  nextSteps: string[];
  actionLabel: string;
  isCompleting?: boolean;
  onContinue: () => void;
}

// The moment a new workspace gets its first channel working. Shown once per channel at the end of onboarding.
export function FirstWinCelebration({ title, titleAs: Title = "h2", body, nextSteps, actionLabel, isCompleting = false, onContinue }: FirstWinCelebrationProps) {
  const reduceMotion = useReducedMotion();
  return (
    <div className="space-y-5">
      <div className="relative flex flex-col items-center pt-2 text-center">
        {!reduceMotion && (
          <div className="pointer-events-none absolute left-1/2 top-9" aria-hidden="true">
            {CONFETTI.map((piece, index) => (
              <motion.span
                key={index}
                className={`absolute h-2 w-1.5 ${piece.round ? "rounded-full" : "rounded-[1px]"}`}
                style={{ backgroundColor: piece.color }}
                initial={{ x: 0, y: 0, opacity: 1, scale: 0.4, rotate: 0 }}
                animate={{ x: piece.x, y: [0, piece.y, piece.y + 120], opacity: [1, 1, 0], scale: 1, rotate: piece.rotate }}
                transition={{ duration: 1.6, delay: 0.15 + piece.delay, ease: "easeOut", times: [0, 0.45, 1] }}
              />
            ))}
          </div>
        )}
        <motion.span
          className="flex h-[72px] w-[72px] items-center justify-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-400/15 dark:text-emerald-300"
          initial={reduceMotion ? false : { scale: 0.4, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", stiffness: 320, damping: 16 }}
        >
          <PartyPopper className="h-9 w-9" aria-hidden="true" />
        </motion.span>
        <div role="status">
          <Title className="mt-4 pr-0 text-xl font-semibold tracking-tight text-balance">{title}</Title>
          <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-muted-foreground">{body}</p>
        </div>
      </div>

      <div className="rounded-[10px] border border-border p-4 text-left">
        <p className="text-sm font-semibold">What to do next</p>
        <ol className="mt-3 space-y-2.5">
          {nextSteps.map((text, index) => (
            <li key={text} className="flex gap-3 text-[13px] leading-relaxed text-muted-foreground">
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#173f2d] text-[10px] font-semibold text-white dark:bg-emerald-300 dark:text-emerald-950">
                {index + 1}
              </span>
              {text}
            </li>
          ))}
        </ol>
      </div>

      <Button type="button" className="w-full" disabled={isCompleting} onClick={onContinue}>
        {isCompleting ? "Finishing…" : actionLabel}
      </Button>
    </div>
  );
}
